import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { VirtualScrollerComponent } from '@iharbeck/ngx-virtual-scroller';
import { CodaltComponent } from '../../codalt.component';
import { CustomerProject } from '../../customer-project.interface';
import { LocalStorage } from '../../local-storage.class';
import { Timeline } from '../../models/timeline.interface';
import { AuthorisationService } from '../../services/auth/authorisation.service';
import { Functionalities } from '../../services/auth/functionalities';
import { CustomerProjectService } from '../../services/customer-project/customer-project.service';
import { CustomerService } from '../../services/customer/customer.service';
import { PageTitleService } from '../../services/page-title/page-title.service';
import { SearchService } from '../../services/search/search.service';
import { TimelineService } from '../../services/timeline.service';

@Component({
    selector: 'app-customer-timeline',
    templateUrl: './customer-timeline.component.html',
    styleUrls: ['./customer-timeline.component.scss'],
    standalone: false,
})
export class CustomerTimelineComponent extends CodaltComponent implements OnInit, OnDestroy {
    @ViewChild('virtualScroll') virtualScroll: VirtualScrollerComponent;

    childActive: boolean;

    timelineItems: Timeline[] = [];
    loading = true;
    projectId;
    customerId;
    project: CustomerProject;

    @ViewChild('projectFilterSearchTemplate', { static: true }) projectFilterSearchTemplate;
    searchProjects: number[] = [];
    projects: CustomerProject[];

    constructor(
        private timelineService: TimelineService,
        private customerProjectService: CustomerProjectService,
        private customerService: CustomerService,
        private searchService: SearchService,
        private cdr: ChangeDetectorRef,
        private pageTitleService: PageTitleService
    ) {
        super();
    }

    ngOnInit(): void {
        this.pageTitleService.setTitle('Tijdlijn');
        if (AuthorisationService.hasFunctionality(Functionalities.TimelineManagement)) {
            this.subscriptions.add(
                this.customerProjectService.getProjects(true).subscribe(projects => {
                    this.projects = projects;
                    this.searchService.searchTemplate = this.projectFilterSearchTemplate;
                })
            );
        }
        this.watchRouteParams(
            [this.RouteParamnames.customerId, this.RouteParamnames.projectId],
            ({ customerId, projectId }) => {
                this.projectId = projectId;
                this.customerId = customerId;
                if (!projectId) {
                    this.searchProjects = LocalStorage.getSearchProjectsIds();
                    this.getTimelines();
                    return;
                }

                // Timelines don't need the customer; consumers get a 403 on customers/{id}
                this.getTimelines();
                if (!AuthorisationService.hasFeature(this.Features.CustomerProjectsDetail)) {
                    return;
                }

                // Eerst het klantgebied uit de route in LocalStorage zetten: de interceptor stuurt
                // current_customer_id daaruit mee. Bij binnenkomst via een link staat daar anders
                // nog een ander klantgebied en geeft getProject() een 404.
                this.subscriptions.add(
                    this.customerService.getSingle(+customerId).subscribe(customer => {
                        LocalStorage.setCustomer(customer);
                        this.subscriptions.add(
                            this.customerProjectService.getProject(+projectId).subscribe(project => {
                                this.project = project.data;
                                this.pageTitleService.setTitle('Tijdlijn', this.project?.name);
                            })
                        );
                    })
                );
            }
        );
    }

    childOpened() {
        this.childActive = true;
        this.cdr.detectChanges();
    }

    childClosed(event) {
        this.pageTitleService.setTitle('Tijdlijn', this.project?.name);
        if (event?.reserve) {
            this.getTimelines();
        } else if (event?.form) {
            const timeline = this.timelineItems.find(a => a.id === event?.timeline?.id);
            if (timeline) {
                Object.assign(timeline, event?.timeline);
            } else {
                this.getTimelines();
            }
        }
    }

    changeSearch() {
        this.getTimelines();
        LocalStorage.setSearchProjectsIds(this.searchProjects);
    }

    ngOnDestroy(): void {
        this.searchService.searchTemplate = null;
        super.ngOnDestroy();
    }

    private getTimelines() {
        this.loading = true;
        if (!this.searchProjects?.length && this.projectId) {
            this.searchProjects = [+this.projectId];
        }
        this.subscriptions.add(
            this.timelineService.getList(this.searchProjects).subscribe(
                timelines => {
                    this.timelineItems = timelines.data;
                    const scrollToItem = this.timelineItems.filter(t => new Date(t.publish_date) <= new Date());
                    if (scrollToItem?.length > 0) {
                        setTimeout(() =>
                            this.virtualScroll?.scrollToIndex(
                                this.timelineItems.indexOf(scrollToItem[scrollToItem.length - 1])
                            )
                        );
                    }
                    this.loading = false;
                },
                () => (this.loading = false)
            )
        );
    }
}
